import React from "react";
import {propertyImportApi} from '../../requests/api';
import Modal from "../Modal/Modal";

var SAMPLE_CSV_HEADERS = [
    // Basic info
    'NAME', 'ADDRESS', 'CITY', 'STATE', 'ZIP', 'STREET', 'LATITUDE', 'LONGITUDE', 'APN',
    // Classification
    'SOURCE', 'TRANSACTION_TYPE', 'STATUS', 'VISIBLE', 'TYPE', 'CATEGORY',
    'SUB_CATEGORY', 'SINGLE_TENANT_SUBCATEGORY', 'MULTI_TENANT_SUBCATEGORY', 'MARKET', 'COUNTY',
    // Building details
    'SQFT', 'UNITS', 'ACRES', 'YEAR_BUILT',
    // Financials
    'LIST_PRICE', 'NET_OPERATING_INCOME', 'VACANCY', 'CAPITALIZATION_RATE',
    'SOLD_PRICE', 'DOWN_PAYMENT', 'BASE_RENT',
    'NEGOTIATION_PRICE', 'NEGOTIATION_CAP_RATE',
    'PROFORMA_NOI', 'PROFORMA_BASE_RENT', 'PROFORMA_CAP_RATE',
    'SELLER_FEE_PERCENTAGE',
    // Dates
    'LISTED_DATE', 'CLOSE_OF_ESCROW', 'DATE_REMOVED',
    // Lease
    'LEASE_TYPE', 'LEASE_COMMENCEMENT_DATE', 'LEASE_EXPIRATION_DATE',
    'EXPIRATION_NO_OF_MONTHS', 'EXPIRATION_DATE', 'EFFECTIVE_DATE',
    // HSC
    'HSC_STATUS', 'HSC_PRICE', 'HSC_LISTED_DATE', 'HSC_FEE_PERCENTAGE',
    'HSC_EXPIRATION_NO_OF_MONTHS', 'HSC_EXPIRATION_DATE', 'HSC_FINANCING',
    // Loan
    'LOAN_TYPE', 'LTV', 'LOAN_AMOUNT', 'INTEREST_RATE',
    'AMORTIZATION', 'TERM', 'MATURITY_DATE',
    'ANNUAL_LOAN_PAYMENT', 'CASH_FLOW', 'CASH_ON_CASH', 'DSCR', 'PREPAYMENT_PENALTY',
    // Referral
    'REFERRAL', 'REFERRAL_PERCENT', 'REFERRAL_FEE', 'REFERRAL_TYPE',
    // Tax / land
    'ASSESSED_VALUE', 'ASSESSED_YEAR', 'TRANS_DATE', 'TRANS_PRICE',
    // Other
    'HIGHLIGHTS', 'NOTES', 'VIDEO_URL', 'FLIP_BOOK', 'MEDIA_KIT',
    'HIDE_FINANCIALS', 'IS_PROFORMA_PRICING', 'ESTIMATED_PRICE_TYPE',
    'CREDITS_AT_CLOSING', 'NOI_PERCENT', 'OTHER_FINANCING',
    'BANK_BONUS', 'BANK_BONUS_PERCENT',
    // Owner — matched against the investors already in iCLIMB, created when
    // there is no match, then attached to the property as a current owner.
    'OWNER_NAME', 'OWNER_EMAIL', 'OWNER_PHONE', 'OWNER_CELL',
    'OWNER_ADDRESS', 'OWNER_CITY', 'OWNER_STATE', 'OWNER_ZIP'
];

var SAMPLE_CSV_ROW = [
    // Basic info
    'Sample Office Building', '123 Main Street', 'Dallas', 'TX', '75201', '', '32.7767', '-96.7970', '',
    // Classification
    // SOURCE is one of SUMMIT / External / HSC / DB — "COSTAR" was left over
    // from this screen's Costar-only days and matches no property in iCLIMB.
    // TYPE is the tenancy (STNL / MT / Other); the asset class is CATEGORY.
    'DB', 'Investment Sales', 'Active', 'y', 'STNL', 'office',
    '', '', '', 'DFW', 'Dallas',
    // Building details
    '15000', '1', '0.5', '2015',
    // Financials
    '1500000', '100000', '5', '6.5',
    '', '', '120000',
    '', '',
    '', '', '',
    '3',
    // Dates
    '2025-01-01', '', '',
    // Lease
    'Full Service', '2025-01-01', '2035-12-31',
    '', '', '',
    // HSC
    '', '', '', '',
    '', '', '',
    // Loan
    '', '', '', '',
    '', '', '',
    '', '', '', '', '',
    // Referral
    '', '', '', '',
    // Tax / land
    '', '', '', '',
    // Other
    'Sample property description', 'Sample notes', '', '', '',
    'n', 'n', '',
    '', '', '',
    '', '',
    // Owner
    'Sample Holdings LLC', 'owner@example.com', '(214) 555-0100', '',
    '500 Main St', 'Dallas', 'TX', '75201'
];

var POLL_INTERVAL_MS = 2000;

/**
 * The run counts lines as it decides them, and records the file's total once
 * it has read it — so there is no percentage to show until that first write.
 */
function percentDone(run) {
    var total = (run.notes || {}).total_rows;

    if (!total) return 0;

    return Math.min(100, Math.round((run.processed / total) * 100));
}

export default class UploadPropertiesTab extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            file: {
                name: null,
                file: null,
                message: '',
                invalidUpload: false
            },
            loading: false,
            submitted: false,
            jobId: null,
            progress: 0,
            jobStatus: null,
            result: null,
            showHelp: false,
        };

        this._pollTimer = null;
        this.uploadFile = this.uploadFile.bind(this);
        this.handleFileChosen = this.handleFileChosen.bind(this);
        this.removeFile = this.removeFile.bind(this);
        this.handleErrors = this.handleErrors.bind(this);
        this.sendFile = this.sendFile.bind(this);
        this.finish = this.finish.bind(this);
        this.showResult = this.showResult.bind(this);
        this.downloadSampleCsv = this.downloadSampleCsv.bind(this);
        this.resetUpload = this.resetUpload.bind(this);
    }

    componentWillUnmount() {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    downloadSampleCsv() {
        var csvContent = SAMPLE_CSV_HEADERS.join(',') + '\n' + SAMPLE_CSV_ROW.join(',') + '\n';
        var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        var url = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'sample-properties.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    uploadFile() {
        if (this._fileInput) this._fileInput.click();
    }

    handleFileChosen(event) {
        var self = this;
        var file = event.target.files[0];

        if (!file) return;

        var reader = new FileReader();

        reader.addEventListener('load', function (loaded) {
            var uploadFileError = self.handleErrors(file.name);
            if (!uploadFileError) {
                var encryption = loaded.target.result;
                var index = encryption.indexOf('base64,') + 'base64,'.length;
                encryption = encryption.replace(encryption.slice(0, index), '');
                self.setState({
                    file: {
                        name: file.name,
                        file: encryption,
                        invalidUpload: false,
                    }
                });
            } else {
                self.setState({
                    file: {
                        name: null,
                        file: null,
                        message: uploadFileError,
                        invalidUpload: true,
                    }
                });
            }
        });
        reader.readAsDataURL(file);
    }

    removeFile() {
        this.setState({
            file: {
                name: null,
                file: null,
                message: ''
            },
            jobId: null,
            progress: 0,
            jobStatus: null,
            result: null,
            submitted: false,
            loading: false
        });
    }

    handleErrors(fileName) {
        return /\.(csv|xlsx)$/i.test(fileName)
            ? null
            : 'Wrong file format! Supported file types: .csv, .xlsx';
    }

    pollJobStatus(jobId) {
        var self = this;
        this._pollTimer = setInterval(function () {
            propertyImportApi.status(jobId)
                .then(function (data) {
                    self.setState({
                        progress: percentDone(data),
                        jobStatus: data.status
                    });

                    if (data.status === 'completed' || data.status === 'failed') {
                        clearInterval(self._pollTimer);
                        self._pollTimer = null;
                        self.finish(data);
                    }
                })
                .catch(function (err) {
                    console.error('[Property upload] Poll error:', err);
                });
        }, POLL_INTERVAL_MS);
    }

    /**
     * The run is over — fetch the lines it skipped, then show the result. The
     * skipped rows are a second request on purpose: they are up to 500 rows,
     * and re-sending them on every two-second poll is what made the old
     * status endpoint slow on a large file.
     */
    finish(status) {
        var self = this;

        propertyImportApi.skippedRows(status.id)
            .then(function (envelope) {
                self.showResult(status, envelope.data || [], envelope.meta || {});
            })
            .catch(function (err) {
                console.error('[Property upload] Could not load skipped rows:', err);
                self.showResult(status, [], {});
            });
    }

    showResult(status, skipped, meta) {
        var notes = status.notes || {};

        this.setState({
            loading: false,
            submitted: true,
            result: {
                total: status.processed,
                inserted: status.created,
                duplicatesCount: status.skipped,
                investorsMatched: notes.investors_matched || 0,
                investorsCreated: notes.investors_created || 0,
                needOwner: notes.need_owner || 0,
                ownersAmbiguous: notes.owners_ambiguous || 0,
                unknownColumns: notes.unknown_columns || [],
                typeReadAsCategory: !!notes.type_read_as_category,
                valuesRejected: notes.values_rejected || 0,
                valuesFlagged: notes.values_flagged || 0,
                errorsCount: status.failed,
                skipped: skipped,
                reviewableTotal: meta.reviewable_total || skipped.length,
                error: status.error,
                status: status.status
            }
        });
    }

    sendFile() {
        var self = this;
        this.setState({ loading: true, progress: 0, jobStatus: 'uploading', result: null, submitted: false });

        var file = this.state.file;

        propertyImportApi.start(file.name, file.file).then(function (data) {
            self.setState({
                jobStatus: data.status,
                progress: data.progress || 0
            });
            self.pollJobStatus(data.id);
        }, function (err) {
            var errorMessage = (err && err.message) || 'Unspecified error';

            console.error('[Property upload] Error:', errorMessage, err);

            self.setState({
                loading: false,
                file: {
                    name: null,
                    file: null,
                    message: errorMessage,
                    invalidUpload: true,
                }
            });
        });
    }

    resetUpload() {
        this.setState({
            file: {
                name: null,
                file: null,
                message: '',
                invalidUpload: false
            },
            loading: false,
            submitted: false,
            jobId: null,
            progress: 0,
            jobStatus: null,
            result: null
        });
    }

    renderProgressBar() {
        var progress = this.state.progress;
        var jobStatus = this.state.jobStatus;

        var statusLabel = 'Starting...';
        if (jobStatus === 'uploading') statusLabel = 'Uploading file...';
        else if (jobStatus === 'queued') statusLabel = 'Queued...';
        else if (jobStatus === 'matching') statusLabel = 'Checking for duplicates...';
        else if (jobStatus === 'importing') statusLabel = 'Adding properties and owners...';

        return (
            <div className='progress-section'>
                <div className='progress-label'>{statusLabel} {progress}%</div>
                <div className='progress-bar-track'>
                    <div className='progress-bar-fill' style={{width: progress + '%'}}/>
                </div>
            </div>
        );
    }

    renderResult() {
        var result = this.state.result;
        if (!result) return null;

        var isFailed = result.status === 'failed' && result.inserted === 0;

        return (
            <div className='result-section'>
                <div className={'result-header ' + (isFailed ? 'result-error' : 'result-success')}>
                    {isFailed
                        ? 'Upload Failed'
                        : 'Upload Complete'
                    }
                </div>

                <div className='result-stats'>
                    <div className='stat'>
                        <span className='stat-number'>{result.total}</span>
                        <span className='stat-label'>Total Rows</span>
                    </div>
                    <div className='stat stat-success'>
                        <span className='stat-number'>{result.inserted}</span>
                        <span className='stat-label'>Inserted</span>
                    </div>
                    <div className='stat stat-warning'>
                        <span className='stat-number'>{result.duplicatesCount}</span>
                        <span className='stat-label'>Skipped (Duplicates)</span>
                    </div>
                    {result.errorsCount > 0 &&
                        <div className='stat stat-error'>
                            <span className='stat-number'>{result.errorsCount}</span>
                            <span className='stat-label'>Errors</span>
                        </div>
                    }
                </div>

                {(result.investorsMatched > 0 || result.investorsCreated > 0) &&
                    <div className='result-stats'>
                        <div className='stat'>
                            <span className='stat-number'>{result.investorsMatched}</span>
                            <span className='stat-label'>Owners Already in CRM</span>
                        </div>
                        <div className='stat stat-success'>
                            <span className='stat-number'>{result.investorsCreated}</span>
                            <span className='stat-label'>Owners Added</span>
                        </div>
                    </div>
                }

                {(result.valuesRejected > 0 || result.valuesFlagged > 0) &&
                    <div className='result-stats'>
                        {result.valuesRejected > 0 &&
                            <div className='stat stat-error'>
                                <span className='stat-number'>{result.valuesRejected}</span>
                                <span className='stat-label'>Values Rejected</span>
                            </div>
                        }
                        {result.valuesFlagged > 0 &&
                            <div className='stat stat-warning'>
                                <span className='stat-number'>{result.valuesFlagged}</span>
                                <span className='stat-label'>Values To Check</span>
                            </div>
                        }
                    </div>
                }

                {result.valuesRejected > 0 &&
                    <div className='result-details result-hint'>
                        Those properties were imported, but the listed fields were left
                        empty because iCLIMB could not read them. Fix them in your file
                        and upload again — nothing will be duplicated.
                        {' '}
                        <button type='button' className='link-button' onClick={this.downloadSampleCsv}>
                            Download the template
                        </button>
                        {' '}to see the format each column expects.
                    </div>
                }

                {result.ownersAmbiguous > 0 &&
                    <div className='result-details result-hint'>
                        <strong>{result.ownersAmbiguous}</strong> {result.ownersAmbiguous === 1 ? 'property names an owner' : 'properties name an owner'}
                        {' '}iCLIMB holds under more than one investor, with no email on the row to
                        tell them apart. Those properties were added and flagged
                        {' '}<strong>NEED OWNER</strong> rather than attached to a guess &mdash; they
                        are listed under Rows To Review. Adding OWNER_EMAIL to those rows and
                        uploading again will link them automatically.
                    </div>
                }

                {result.typeReadAsCategory &&
                    <div className='result-details result-hint'>
                        Your <strong>Type</strong> column held an asset class, so it was read as
                        <strong> Category</strong>. In iCLIMB, Type is the tenancy
                        (Single-Tenant / Multi-Tenant / Other) and Category is the asset class
                        (Industrial, Office, Retail, Multi-Family, Other).
                    </div>
                }

                {result.unknownColumns.length > 0 &&
                    <div className='result-details result-hint'>
                        These columns are not part of iCLIMB and were ignored:{' '}
                        <strong>{result.unknownColumns.join(', ')}</strong>.
                        {' '}
                        <button type='button' className='link-button' onClick={this.downloadSampleCsv}>
                            Download the template
                        </button>
                        {' '}and use its header row so nothing is missed.
                    </div>
                }

                {result.error &&
                    <div className='result-details details-error'>{result.error}</div>
                }

                {result.reviewableTotal > 0 &&
                    <div className='result-details'>
                        <div className='details-toggle' onClick={this._toggleDuplicates}>
                            Rows To Review ({result.reviewableTotal})
                        </div>
                        {this.state.showDuplicates &&
                            <div className='details-list'>
                                {result.skipped.map(function (row, i) {
                                    var notes = row.notes || [];
                                    // Only a note about a value carries a level;
                                    // the first note says where the property is.
                                    var place = notes.filter(function (n) { return !n.level; })[0] || {};
                                    var issues = notes.filter(function (n) { return n.level; });
                                    var wentIn = row.outcome === 'created';

                                    return (
                                        <div
                                            key={i}
                                            className={'details-item' + (row.outcome === 'failed' ? ' details-error-item' : '')}
                                        >
                                            <div>
                                                Row {row.external_id}: {place.value}
                                                {place.text ? ', ' + place.text : ''}
                                                {wentIn ? '' : ' — ' + row.reason}
                                            </div>
                                            {issues.map(function (issue, j) {
                                                return (
                                                    <div
                                                        key={j}
                                                        className={'details-issue details-issue--' + issue.level}
                                                    >
                                                        {issue.column} “{issue.value}” {issue.text}
                                                        {issue.level === 'error' ? ' — left empty' : ''}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                                {result.reviewableTotal > result.skipped.length &&
                                    <div className='details-item'>
                                        ...and {result.reviewableTotal - result.skipped.length} more
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }

                <button type='button' className='button' onClick={this.resetUpload}>
                    Upload Another File
                </button>
            </div>
        );
    }

    render() {
        var deleteIcon = 'url("' + arc.path.media + 'delete.svg")';
        var file = this.state.file;
        var loading = this.state.loading;
        var submitted = this.state.submitted;
        var readyToSubmit = file.file && !file.invalidUpload && !loading && !submitted;
        var showHelp = this.state.showHelp;
        var self = this;

        this._toggleDuplicates = function () {
            this.setState({ showDuplicates: !this.state.showDuplicates });
        }.bind(this);

        this._toggleErrors = function () {
            this.setState({ showErrors: !this.state.showErrors });
        }.bind(this);

        return (
            <div>
                <div className='upload-title-row'>
                    <div className='upload-file-title'>Upload Properties</div>
                    <button
                        type='button'
                        className='upload-help-btn'
                        onClick={function () { this.setState({ showHelp: true }); }.bind(this)}
                        title='How does this upload work?'
                    >
                        ?
                    </button>
                </div>

                <div className='upload-instructions'>
                    <p>Upload a CSV or Excel (.xlsx) file to import properties into iSummit. A
                    property that iCLIMB already holds &mdash; same parcel number, or same
                    address in the same ZIP &mdash; is skipped. Fill in the OWNER_ columns and
                    each property is linked to its owner as well.</p>
                    <p>The sample below is the template iCLIMB knows best. A file exported from
                    another system also works when its headers name the same things &mdash;
                    &ldquo;Address 1&rdquo;, &ldquo;Zip code&rdquo;, &ldquo;Assessed Value&rdquo;
                    and the like are matched automatically. Anything iCLIMB has no column for is
                    listed after the upload rather than dropped quietly.</p>
                    <button type='button' className='button sample-btn' onClick={this.downloadSampleCsv}>
                        Download Sample CSV
                    </button>
                </div>

                <Modal
                    show={showHelp}
                    onClose={function () { self.setState({ showHelp: false }); }}
                    className='upload-help-modal'
                >
                   <div className='upload-help-content'>
                        <div className='upload-help-header'>
                            <h2 className='upload-help-title'>Upload Properties &mdash; Help</h2>
                            <button
                                className='upload-help-close'
                                onClick={function () { self.setState({ showHelp: false }); }}
                            >&times;</button>
                        </div>
                        <div className='upload-help-body'>
                            <p>
                                Use this page to add new properties to iCLIMB in bulk from a CSV file.
                                It is meant for situations where you have a list of properties in a
                                spreadsheet and want to create them all at once instead of adding them
                                one by one.
                            </p>

                            <h3>How it works</h3>

                            <div className='upload-help-item'>
                                <h4>1. Download the sample CSV</h4>
                                <p>
                                    Click <strong>Download Sample CSV</strong>. The sample has every
                                    column iCLIMB understands, in the right order. Use it as your
                                    template so the upload knows where each value belongs.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>2. Fill in your rows</h4>
                                <p>
                                    One property per row. Leave a cell blank if you don&rsquo;t have a
                                    value &mdash; the property will simply be created without that
                                    field. Don&rsquo;t rename or reorder the header row.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>3. Pick your file</h4>
                                <p>
                                    Click <strong>Browse&hellip;</strong> and choose your file.
                                    Both <strong>.csv</strong> and <strong>.xlsx</strong> are
                                    supported. If you have the workbook, upload the .xlsx rather
                                    than saving it as CSV first &mdash; Excel is what turns a long
                                    parcel number into 6.5E+16 on the way out.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>4. Submit</h4>
                                <p>
                                    Click <strong>Submit</strong>. A progress bar shows how many rows
                                    have been processed. You can leave this tab open while it runs.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>5. Review the result</h4>
                                <p>
                                    When it finishes you&rsquo;ll see how many properties were added,
                                    how many were skipped as duplicates, and any rows that had errors.
                                    Expand each section to see the details.
                                </p>
                            </div>

                            <h3>Owners</h3>
                            <p>
                                Fill in <strong>OWNER_NAME</strong> (and optionally
                                OWNER_EMAIL, OWNER_PHONE, OWNER_CELL and the OWNER address
                                columns) and the upload will connect the property to its owner.
                                It looks for that owner among the investors iCLIMB already has
                                &mdash; by email first, then by name &mdash; and only creates a
                                new investor when there is no match, so the same owner across
                                fifty rows stays one record. Each imported property gets a
                                current ownership period holding that investor, and the link is
                                pushed to the CRM.
                            </p>
                            <p>
                                Leave OWNER_NAME blank and the property is still created; it
                                just has no owner attached.
                            </p>

                            <h3>How duplicates are handled</h3>
                            <p>
                                A row is skipped when it describes a property iCLIMB already
                                holds: the same <strong>parcel number (APN)</strong> in the same
                                state, or the same <strong>address inside the same ZIP</strong>.
                                Addresses are compared by meaning, so &ldquo;1197 Interstate
                                35&rdquo; and &ldquo;1197 I-35&rdquo; count as the same building.
                                Rows are checked against each other too, so a file that lists one
                                property twice imports it once.
                            </p>

                            <h3>Keep in mind</h3>
                            <ul className='upload-help-tips'>
                                <li>
                                    The upload <strong>only adds new properties</strong>. It never
                                    edits or overwrites a property that is already in iCLIMB.
                                </li>
                                <li>
                                    Every property it adds is stamped <strong>UPLOADS</strong> with
                                    the date, so a batch can be found later by filtering on Created
                                    By and Date Created.
                                </li>
                                <li>
                                    Headers do not have to match the template exactly. A header
                                    that names the same thing in another spelling &mdash;
                                    &ldquo;Address 1&rdquo;, &ldquo;Property name&rdquo;,
                                    &ldquo;Tax Acct #&rdquo;, &ldquo;Owner.Company&rdquo; &mdash;
                                    is matched to the right column, and whatever is left over is
                                    named on the result screen.
                                </li>
                                <li>
                                    If a row has a bad value (for example an unreadable date) it
                                    will be listed under errors &mdash; fix it in the file and
                                    upload again. Already-added rows will not be duplicated.
                                </li>
                                <li>
                                    For uploading Costar comps, use the <strong>Comps</strong> tab
                                    instead &mdash; it has a different set of rules.
                                </li>
                            </ul>
                        </div>
                    </div>
                </Modal>

                {/* In the DOM rather than built on the fly, so the picker can
                    be driven by a test as well as by a person. */}
                <input
                    type='file'
                    accept='.csv,.xlsx'
                    name='file'
                    ref={function (el) { self._fileInput = el; }}
                    onChange={this.handleFileChosen}
                    style={{display: 'none'}}
                />

                {!submitted && !loading &&
                    <div>
                        {!file.file &&
                            <button type='button' className='button' onClick={this.uploadFile}>
                                Browse...
                            </button>
                        }
                    </div>
                }

                <div className='upload-file-details'>
                    {file.file && !loading && !submitted &&
                        <div className='upload-file-text'>{file.name}</div>
                    }
                    {file.invalidUpload &&
                        <div className='upload-file-text' style={{color: '#FF5050'}}>{file.message}</div>
                    }
                    {file.file && !loading && !submitted &&
                        <div onClick={this.removeFile} className='button delete' style={{backgroundImage: deleteIcon}}/>
                    }
                </div>

                {loading && this.renderProgressBar()}

                {submitted && this.renderResult()}

                {readyToSubmit &&
                    <button type='button' className='button submit' onClick={this.sendFile}>Submit</button>
                }
            </div>
        );
    }
}
